import { LiveChatWidget, LiveChatWidgetState, LiveChatWidgetVisibilityChangeData } from 'shared/models/LiveChat'
import { parseLiveChatUrl } from 'shared/utils/agentSocialLinks'

const DEFAULT_LICENSE = '19944332'
const TRACKING_SRC = 'https://cdn.livechatinc.com/tracking.js'

type LiveChatBootConfig = {
  license: string
  group?: string
}

let bootedConfig: LiveChatBootConfig | null = null
let isWidgetReady = false
let isOpenRequested = false

const WIDGET_ACTIVATED_CLASS = 'live-chat-activated'

const getBootConfig = (link?: string): LiveChatBootConfig => {
  const parsed = link ? parseLiveChatUrl(link) : null

  return {
    license: parsed?.license || DEFAULT_LICENSE,
    group: parsed?.group,
  }
}

const isSameConfig = (left: LiveChatBootConfig | null, right: LiveChatBootConfig) =>
  left?.license === right.license && left?.group === right.group

const clearLiveChatStorage = () => {
  const expire = 'Thu, 01 Jan 1970 00:00:00 GMT'

  document.cookie.split(';').forEach(part => {
    const name = part.split('=')[0]?.trim()
    if (!name) return

    const normalized = name.toLowerCase()
    if (!normalized.startsWith('__lc') && !normalized.startsWith('lc_') && !normalized.includes('livechat')) {
      return
    }

    document.cookie = `${name}=; expires=${expire}; path=/`
  })
  ;[localStorage, sessionStorage].forEach(storage => {
    Object.keys(storage).forEach(key => {
      const normalized = key.toLowerCase()
      if (normalized.startsWith('__lc') || normalized.includes('livechat')) {
        storage.removeItem(key)
      }
    })
  })
}

const setWidgetActivated = (isActivated: boolean) => {
  document.documentElement.classList.toggle(WIDGET_ACTIVATED_CLASS, isActivated)
}

const hideNativeBubble = () => {
  window.LiveChatWidget?.call('hide')
}

const maximizeChat = () => {
  window.LiveChatWidget?.call('hide_greeting')
  window.LiveChatWidget?.call('maximize')
}

const handleReady = ({ state }: { state: LiveChatWidgetState }) => {
  isWidgetReady = true

  if (isOpenRequested) {
    isOpenRequested = false
    maximizeChat()
    return
  }

  if (state.visibility === 'maximized') {
    setWidgetActivated(true)
    return
  }

  hideNativeBubble()
}

const handleVisibilityChanged = ({ visibility }: LiveChatWidgetVisibilityChangeData) => {
  setWidgetActivated(visibility === 'maximized')

  if (visibility === 'minimized') {
    hideNativeBubble()
  }
}

const teardownLiveChat = () => {
  try {
    window.LiveChatWidget?.call('destroy')
  } catch {
    // widget may already be gone
  }

  document
    .querySelectorAll(
      [
        'script[src*="livechatinc.com"]',
        'script[src*="livechat.com"]',
        'iframe[src*="livechatinc.com"]',
        'iframe[src*="livechat.com"]',
        'iframe[src*="lc.chat"]',
        '#chat-widget-container',
      ].join(', '),
    )
    .forEach(node => node.remove())

  clearLiveChatStorage()

  delete window.LC_API
  delete window.LiveChatWidget
  delete window.__lc
  bootedConfig = null
  isWidgetReady = false
  setWidgetActivated(false)
}

const createLiveChatSnippet = (config: LiveChatBootConfig) => {
  window.__lc = {
    license: Number(config.license) || config.license,
    integration_name: 'manual_channels',
    product_name: 'livechat',
  }

  if (config.group) {
    window.__lc.group = Number(config.group) || config.group
  }

  const widget: {
    _q: unknown[][]
    _h: ((...args: unknown[]) => unknown) | null
    _v: string
    on: (...args: unknown[]) => unknown
    once: (...args: unknown[]) => unknown
    off: (...args: unknown[]) => unknown
    get: (...args: unknown[]) => unknown
    call: (...args: unknown[]) => unknown
    init: () => void
  } = {
    _q: [],
    _h: null,
    _v: '2.0',
    on: (...args: unknown[]) => enqueue(['on', args]),
    once: (...args: unknown[]) => enqueue(['once', args]),
    off: (...args: unknown[]) => enqueue(['off', args]),
    get: (...args: unknown[]) => {
      if (!widget._h) throw new Error("[LiveChatWidget] You can't use getters before load.")
      return enqueue(['get', args])
    },
    call: (...args: unknown[]) => enqueue(['call', args]),
    init: () => {
      const script = document.createElement('script')
      script.async = true
      script.type = 'text/javascript'
      script.src = TRACKING_SRC
      document.head.appendChild(script)
    },
  }

  function enqueue(args: unknown[]) {
    if (widget._h) {
      return widget._h(...args)
    }

    widget._q.push(args)
    return undefined
  }

  window.LiveChatWidget = widget as unknown as LiveChatWidget
  window.LiveChatWidget.on('ready', handleReady)
  window.LiveChatWidget.on('visibility_changed', handleVisibilityChanged)
  hideNativeBubble()

  widget.init()
}

export const bootLiveChat = (link?: string) => {
  const config = getBootConfig(link)

  if (isSameConfig(bootedConfig, config) && window.LiveChatWidget) {
    return
  }

  teardownLiveChat()
  createLiveChatSnippet(config)
  bootedConfig = config
}

export const openChat = () => {
  if (!isWidgetReady) {
    isOpenRequested = true
    return
  }

  maximizeChat()
}

export const shutdownLiveChat = () => {
  isOpenRequested = false

  if (!bootedConfig && !window.LiveChatWidget) return

  teardownLiveChat()
}
